import React, { FormEvent, PureComponent, ReactNode } from "react";
import styled from "styled-components";
import { ObjectSchema } from "yup";

import theme from "../../../theme";
import Button from "../../Button";
import Input from "../Input";

const Form = styled.form`
  margin-top: ${theme.margins.marginBase};
`;

const StyledButton = styled(Button)`
  margin-top: ${theme.margins.marginBase};
`;

interface IProps {
  buttonLabel?: string;
  className?: string;
  defaultValue?: string;
  fieldName: string;
  inputType: string;
  loading?: boolean;
  onButtonClick: (field: string) => Promise<void>;
  placeholder?: string;
  validationSchema?: ObjectSchema<{}>;
}

interface IState {
  fieldError: string | null;
  fieldTouched: boolean;
  fieldValue: string;
}

export default class SingleFieldForm extends PureComponent<IProps, IState> {
  public static Input: typeof Input.Input = Input.Input;
  public static Button: typeof StyledButton = StyledButton;

  public static defaultProps: Partial<IProps> = {
    className: "",
    loading: false,
  };

  public state: IState = {
    fieldError: null,
    fieldTouched: false,
    fieldValue: this.props.defaultValue || "",
  };

  public checkFieldValidity = (field: string): boolean => {
    try {
      const { fieldName, validationSchema } = this.props;
      if (validationSchema) {
        validationSchema.validateSync({ [fieldName]: field });
        this.setState({ fieldError: null });
      }

      return true;
    } catch (e) {
      this.setState({ fieldError: e.message });

      return false;
    }
  };

  public handleButtonClick = async (): Promise<void> => {
    const { fieldValue, fieldTouched } = this.state;

    if (this.checkFieldValidity(fieldValue)) {
      this.props
        .onButtonClick(fieldValue)
        .then(() => this.setState({ fieldValue: "" }));
    } else if (!fieldTouched) {
      this.setState({ fieldTouched: true });
    }
  };

  public handleFieldBlur = (): void => {
    if (!this.state.fieldTouched) {
      this.setState({ fieldTouched: true });
      this.checkFieldValidity(this.state.fieldValue);
    }
  };

  public handleFieldChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const fieldValue = event.target.value;
    if (this.state.fieldTouched) {
      this.checkFieldValidity(fieldValue);
    }
    this.setState({ fieldValue });
  };

  public handleFormSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
  };

  public render(): ReactNode {
    const {
      buttonLabel,
      className,
      fieldName,
      inputType,
      placeholder,
      loading,
    } = this.props;
    const { fieldError, fieldValue } = this.state;

    return (
      <Form className={className} onSubmit={this.handleFormSubmit}>
        <Input
          type={inputType}
          value={fieldValue}
          onBlur={this.handleFieldBlur}
          onChange={this.handleFieldChange}
          placeholder={placeholder}
          name={fieldName}
          invalid={fieldError != null}
          errorMessage={fieldError}
        />
        <StyledButton
          fullWidth
          title={buttonLabel || "Envoyer"}
          onClick={this.handleButtonClick}
          loading={loading}
        />
      </Form>
    );
  }
}
